/**
 * Projects Management Page
 * 
 * 案件情報の管理ページ
 * 主な機能:
 * - 案件一覧表示と検索
 * - 新規案件の登録
 * - 案件情報の編集と削除
 * - ステータスや優先度によるフィルタリング
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  IconButton,
  Menu,
  MenuItem as MenuItemComponent,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  LocationOn,
  Schedule,
  Visibility,
  Business,
  Close as CloseIcon,
  Code,
  MoreVert,
  Send,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { projectsService } from '../services/supabase';
import { supabase } from '../config/supabase';
import { ProjectWithSkills, CreateProjectRequest } from '../types';
import ConfirmDialog from '../components/ConfirmDialog';
import { validateRequired, validateAndSanitizeInput } from '../utils/validation';
import { getStatusColor, getStatusText, getPriorityColor, getPriorityText, getYearsText } from '../utils/helpers';
import { commonSkills } from '../constants/skills';


const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectWithSkills | null>(null);
  
  // Supabase state
  const [projects, setProjects] = useState<ProjectWithSkills[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Form state
  const [formData, setFormData] = useState<CreateProjectRequest>({
    title: '',
    description: '',
    client_name: '',
    budget: 0,
    start_date: '',
    end_date: '',
    status: 'pending',
    priority: 'medium',
    location: '',
    remote_work: false,
    skills: []
  });

  // Skill management state
  const [skillInput, setSkillInput] = useState({
    skill_name: '',
    required_years: 1,
    is_required: false
  });

  // Load projects data
  useEffect(() => {
    loadProjects();
    
    // Supabaseのリアルタイム購読を設定
    const subscription = supabase
      .channel('projects_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'projects' },
        (payload: any) => {
          loadProjects(); // データを再読み込み
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    const result = await projectsService.getAll();
    if (result.error) {
      setError(result.error);
      setSnackbar({ open: true, message: `データの取得に失敗しました: ${result.error}`, severity: 'error' });
    } else {
      setProjects(result.data || []);
    }
    setLoading(false);
  };

  // Skill management functions
  const addSkill = () => {
    if (!skillInput.skill_name.trim()) {
      setSnackbar({ open: true, message: 'スキル名を入力してください', severity: 'error' });
      return;
    }

    // Validate required years
    if (skillInput.required_years < 0 || skillInput.required_years > 20) {
      setSnackbar({ open: true, message: '要求年数は0〜20年の範囲で入力してください', severity: 'error' });
      return;
    }

    // Check if skill already exists
    if (formData.skills?.some(skill => skill.skill_name.toLowerCase() === skillInput.skill_name.toLowerCase())) {
      setSnackbar({ open: true, message: 'このスキルは既に追加されています', severity: 'error' });
      return;
    }

    setFormData(prev => ({
      ...prev,
      skills: [...(prev.skills || []), { ...skillInput }]
    }));

    // Reset skill input
    setSkillInput({
      skill_name: '',
      required_years: 1,
      is_required: false
    });
  };

  const removeSkill = (skillName: string) => {
    setFormData(prev => ({
      ...prev,
      skills: (prev.skills || []).filter(skill => skill.skill_name !== skillName)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      client_name: '',
      budget: 0,
      start_date: '',
      end_date: '',
      status: 'pending',
      priority: 'medium',
      location: '',
      remote_work: false,
      skills: []
    });
    setSkillInput({
      skill_name: '',
      required_years: 1,
      is_required: false
    });
  };

  const handleCreateProject = async () => {
    try {
      // Validate required fields
      const titleValidation = validateRequired(formData.title, '案件名');
      if (!titleValidation.isValid) {
        setSnackbar({ open: true, message: titleValidation.errors[0], severity: 'error' });
        return;
      }

      // Validate at least one skill is provided
      if (!formData.skills || formData.skills.length === 0) {
        setSnackbar({ open: true, message: '少なくとも1つのスキルを追加してください', severity: 'error' });
        return;
      }

      // Sanitize inputs
      const sanitizedData = {
        ...formData,
        title: validateAndSanitizeInput(formData.title, 200),
        description: formData.description ? validateAndSanitizeInput(formData.description, 2000) : '',
        client_name: formData.client_name ? validateAndSanitizeInput(formData.client_name, 200) : '',
        location: formData.location ? validateAndSanitizeInput(formData.location, 255) : '',
        start_date: formData.start_date && formData.start_date.trim() !== '' ? formData.start_date : undefined,
        end_date: formData.end_date && formData.end_date.trim() !== '' ? formData.end_date : undefined,
        skills: formData.skills?.filter(skill => skill.skill_name.trim() !== '') || []
      };

      const result = await projectsService.create(sanitizedData);
      if (result.error) {
        setSnackbar({ open: true, message: `案件の登録に失敗しました: ${result.error}`, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: '案件を登録しました', severity: 'success' });
        setOpenDialog(false);
        resetForm();
        loadProjects(); // Refresh data
      }
    } catch (error) {
      setSnackbar({ open: true, message: '案件の登録中にエラーが発生しました', severity: 'error' });
      console.error('Project creation error:', error);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; project: { id: number; title: string } | null }>({
    open: false,
    project: null
  });

  const handleDeleteProject = (id: number, title: string) => {
    setDeleteConfirm({ open: true, project: { id, title } });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.project) return;

    const result = await projectsService.delete(deleteConfirm.project.id);
    if (result.error) {
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    } else {
      setSnackbar({ open: true, message: '案件を削除しました', severity: 'success' });
      loadProjects(); // Refresh data
    }
    setDeleteConfirm({ open: false, project: null });
  };


  // 単一の .filter() に統合（js-combine-iterations ルール適用）
  const filteredProjects = projects.filter(project => {
    if (filterStatus !== 'all' && project.status !== filterStatus) return false;
    if (filterPriority !== 'all' && project.priority !== filterPriority) return false;
    const searchLower = searchTerm.toLowerCase();
    return project.title.toLowerCase().includes(searchLower) ||
           (project.client_name && project.client_name.toLowerCase().includes(searchLower)) ||
           project.skills.some(skill => skill.skill_name.toLowerCase().includes(searchLower));
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, project: ProjectWithSkills) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          案件管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
          sx={{ bgcolor: '#27ae60' }}
        >
          案件登録
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 検索・フィルター */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              placeholder="案件名・会社名・スキルで検索"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>ステータス</InputLabel>
              <Select
                value={filterStatus}
                label="ステータス"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="pending">準備中</MenuItem>
                <MenuItem value="active">募集中</MenuItem>
                <MenuItem value="completed">完了</MenuItem>
                <MenuItem value="cancelled">キャンセル</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>優先度</InputLabel>
              <Select
                value={filterPriority}
                label="優先度"
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="urgent">緊急</MenuItem>
                <MenuItem value="high">高</MenuItem>
                <MenuItem value="medium">中</MenuItem>
                <MenuItem value="low">低</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* 案件一覧 */}
      <Box sx={{ display: 'grid', gap: 1 }}>
        {filteredProjects
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((project) => (
            <Link 
            to={`/project/${project.id}`} 
            style={{ textDecoration: 'none', color: 'inherit' }}
            key={project.id}
          >
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                borderLeft: `4px solid ${getPriorityColor(project.priority)}`,
                '&:hover': { 
                  transform: 'translateY(-2px)', 
                  boxShadow: 3 
                }
              }}
            >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 2, alignItems: 'start' }}>
                <Box>
                  {/* ヘッダー部分 */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {project.title}
                        </Typography>
                        <Chip
                          label={getPriorityText(project.priority)}
                          size="small"
                          sx={{
                            bgcolor: getPriorityColor(project.priority),
                            color: 'white',
                            fontWeight: 'bold',
                            height: 20,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                      {project.client_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Business fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            {project.client_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleMenuClick(e, project);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {/* スキル */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {project.skills.slice(0, 4).map((skill) => (
                      <Chip
                        key={skill.id}
                        label={`${skill.skill_name} (${skill.required_years}年)`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    ))}
                    {project.skills.length > 4 && (
                      <Chip
                        label={`+${project.skills.length - 4}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>

                  {/* メタ情報 */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 1 }}>
                    {project.budget != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption">
                          ¥{project.budget.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    {project.start_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule fontSize="small" color="action" />
                        <Typography variant="caption">
                          {new Date(project.start_date).toLocaleDateString()}開始
                        </Typography>
                      </Box>
                    )}
                    {project.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="caption">
                          {project.location} {project.remote_work && '(リモート可)'}
                        </Typography>
                      </Box>
                    )}
                    {project.assignments && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Send fontSize="small" color="action" />
                        <Typography variant="caption">
                          アサイン: {project.assignments.length}件
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* ステータス・アクション */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  <Chip
                    label={getStatusText(project.status)}
                    color={getStatusColor(project.status) as any}
                    size="small"
                    sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                  />
                  {project.start_date && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                      開始: {new Date(project.start_date).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                    登録: {new Date(project.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            </Card>
            </Link>
          ))}
      </Box>

      {/* ページネーション */}
      <Card sx={{ mt: 3 }}>
        <TablePagination
          component="div"
          count={filteredProjects.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Card>

      {/* コンテキストメニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItemComponent onClick={() => {
          if (selectedProject) navigate(`/project/${selectedProject.id}`);
          handleMenuClose();
        }}>
          <Visibility sx={{ mr: 1 }} />
          詳細表示
        </MenuItemComponent>
        <MenuItemComponent onClick={() => {
          if (selectedProject) navigate(`/project/${selectedProject.id}`);
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1 }} />
          編集
        </MenuItemComponent>
        <Divider />
        <MenuItemComponent onClick={() => {
          if (selectedProject) {
            handleDeleteProject(selectedProject.id, selectedProject.title);
          }
          handleMenuClose();
        }} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          削除
        </MenuItemComponent>
      </Menu>

      {/* 新規登録ダイアログ */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>新しい案件を登録</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <TextField 
              fullWidth 
              label="案件名" 
              required 
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField 
              fullWidth 
              label="クライアント名" 
              value={formData.client_name}
              onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="案件詳細"
              multiline
              rows={3}
              placeholder="案件の詳細を入力してください"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField 
                fullWidth 
                label="開始予定日" 
                type="date" 
                InputLabelProps={{ shrink: true }}
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
              />
              <TextField 
                fullWidth 
                label="終了予定日" 
                type="date" 
                InputLabelProps={{ shrink: true }}
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField 
                fullWidth 
                label="予算（円）" 
                type="number" 
                value={formData.budget || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, budget: parseInt(e.target.value) || 0 }))}
              />
              <TextField 
                fullWidth 
                label="勤務地" 
                placeholder="東京都千代田区" 
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>優先度</InputLabel>
              <Select 
                label="優先度" 
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
              >
                <MenuItem value="urgent">緊急</MenuItem>
                <MenuItem value="high">高</MenuItem>
                <MenuItem value="medium">中</MenuItem>
                <MenuItem value="low">低</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>ステータス</InputLabel>
              <Select 
                label="ステータス" 
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
              >
                <MenuItem value="pending">準備中</MenuItem>
                <MenuItem value="active">募集中</MenuItem>
                <MenuItem value="completed">完了</MenuItem>
                <MenuItem value="cancelled">キャンセル</MenuItem>
              </Select>
            </FormControl>
            
            {/* リモートワーク設定 */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.remote_work}
                  onChange={(e) => setFormData(prev => ({ ...prev, remote_work: e.target.checked }))}
                />
              }
              label="リモートワーク可能"
            />

            {/* 必要スキル・要件 */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <Code sx={{ mr: 1 }} />
                必要スキル・要件
              </Typography>
              
              {/* スキル追加フォーム */}
              <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Autocomplete
                    freeSolo
                    options={commonSkills}
                    value={skillInput.skill_name}
                    onInputChange={(event, newValue) => {
                      setSkillInput(prev => ({ ...prev, skill_name: newValue }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="スキル名"
                        placeholder="スキル名を入力してください"
                        fullWidth
                      />
                    )}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 2, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      label="要求年数"
                      type="number"
                      value={skillInput.required_years}
                      onChange={(e) => setSkillInput(prev => ({ 
                        ...prev, 
                        required_years: parseInt(e.target.value) || 1
                      }))}
                      inputProps={{ min: 0, max: 20 }}
                      helperText="年"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={skillInput.is_required}
                          onChange={(e) => setSkillInput(prev => ({ ...prev, is_required: e.target.checked }))}
                        />
                      }
                      label="必須スキル"
                    />
                    <Button 
                      variant="contained" 
                      onClick={addSkill}
                      disabled={!skillInput.skill_name.trim()}
                    >
                      追加
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* 追加されたスキル一覧 */}
              {formData.skills && formData.skills.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'textSecondary' }}>
                    追加されたスキル ({formData.skills?.length}件)
                  </Typography>
                  <List dense>
                    {formData.skills?.map((skill, index) => (
                      <ListItem key={`skill-${index}-${skill.skill_name}`} sx={{ px: 1, py: 0.5, bgcolor: '#f5f5f5', mb: 0.5, borderRadius: 1 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {skill.skill_name}
                              </Typography>
                              <Chip
                                label={getYearsText(skill.required_years)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {skill.is_required && (
                                <Chip
                                  label="必須"
                                  size="small"
                                  color="error"
                                  variant="filled"
                                />
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => removeSkill(skill.skill_name)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleCreateProject}>
            登録
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="案件削除"
        message={`「${deleteConfirm.project?.title}」を削除しますか？この操作は取り消せません。`}
        type="warning"
        confirmText="削除"
        cancelText="キャンセル"
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ open: false, project: null })}
      />
    </Box>
  );
};

export default Projects;